import { collection, addDoc, Timestamp, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { where as liteWhere } from "firebase/firestore/lite";
import db, { auth, functions } from "../Firebase.js";
import { UserActivity, UserIPData } from "../../models/UserActivity.js";
import { httpsCallable } from "firebase/functions";
import { getLiteQueryDocs } from "../../helpers/firestoreLiteQuery.ts";
import { Users } from "../../models/User.tsx";

/**
 * Authenticates a user by verifying the provided username and PIN against the database.
 * @param {string} userName - The username of the user attempting to authenticate.
 * @param {string} pin - The PIN associated with the username.
 * @returns A Promise that resolves to the user data if authentication is successful.
 * @throws Error if the provided username does not exist or if the PIN is incorrect.
 */

export async function singUp(newUser: Partial<Users>): Promise<void> {
    const [userNameDocs, emailDocs] = await Promise.all([
        getLiteQueryDocs('users', [liteWhere('userName', '==', newUser.userName)]),
        getLiteQueryDocs('users', [liteWhere('email', '==', newUser.email)]),
    ]);

    if (userNameDocs.length > 0 || emailDocs.length > 0) {
        throw new Error('Username or email is already in use');
    }
    newUser.isOnline = false;
    newUser.parent = '';
    newUser.available = 0;
    newUser.balance = 0;
    newUser.wagerLimit = 0;
    newUser.creditLimit = 0;
    newUser.status = 'Active';
    newUser.isActive = 'Active';
    newUser.pending = 0.00;
    newUser.img = '';
    newUser.isLogin = false;


    await addDoc(collection(db, 'users'), newUser);
}

export async function passwordRecoveryUser(email: string): Promise<void> {
    const userEmail = await getLiteQueryDocs('users', [
        liteWhere('email', '==', email),
    ]);

    let userId: string | null = null;

    userEmail.forEach(docSnap => {
        userId = docSnap.id;
    });

    if (userId) {
        const notificationRef = collection(db, 'notifications');
        await addDoc(notificationRef, {
            title: "Password Recovery",
            description: "Password recovery request for the user",
            directedTo: "Administrator",
            userId: userId,
        });
    }
}


export async function fetchUser({ docId, uid }: { docId?: string, uid?: string }): Promise<Users> {
    if (!docId && !uid) {
        throw new Error("Either docId or uid must be provided");
    }

    if (docId && uid) {
        throw new Error("Only docId or uid must be provided");
    }

    try {
        const userData = httpsCallable<{ docId?: string, uid?: string, isPublic?: boolean }, Users>(
            functions,
            "fetchUser"
        );

        const result = await userData({ docId, uid, isPublic: false });
        return result.data as Users;
    } catch (error) {
        console.error("Error function:", error);
        throw error;
    }
}


export async function editUser(userId: string, newData: Partial<Users>): Promise<void> {
    try {
        const UserDoc = doc(db, 'users', userId);
        await updateDoc(UserDoc, newData);
    } catch (error) {
        console.error(error);
        throw error;
    }
}

export async function updateUserImage(userName: string, imageUrl: string): Promise<void> {
    if (!userName) {
        throw new Error('No active user ID found in localStorage');
    }
    const userRef = doc(db, 'users', userName);
    await updateDoc(userRef, {
        img: imageUrl
    });
}

const isMobileDevice = () => {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

export const logUserActivity = async (userName: string, action: string, ipData?: UserIPData) => {

    const finalAction = isMobileDevice() ? `(M) ${action}` : action;
    const activity: UserActivity = {
        userName,
        dateTime: serverTimestamp() as Timestamp,
        action: finalAction,
    };
    if (ipData) {
        activity.IpData = ipData;
    }

    if (!auth.currentUser) {
        return;
    }

    try {
        await addDoc(collection(db, "userActivity"), activity);
    } catch (error) {
        console.error(error);
    }
};

export const setUserIdentity = async (docId: string, uid: string) => {
    const userRef = doc(db, `users/${docId}`);
    await updateDoc(userRef, { uid });
}